import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrgMember } from 'src/pages/OrgSettings/lastOwner';
import { authClient } from 'src/services/auth/client';
import { QUERIES } from 'src/utils/react-query';

export type OrgMemberRow = OrgMember & { user: { name: string; email: string }; createdAt: string };

/**
 * Plain functions beneath the hooks, so `@restavo/crud` can reach these endpoints too — its
 * `api` slots take async functions, and a React hook cannot be called from one. The hooks
 * below are the only callers besides the CRUD adapters, so there is still one definition of
 * each request.
 */
export async function fetchOrgMembers(organizationId: string): Promise<OrgMemberRow[]> {
  const { data, error } = await authClient.organization.getFullOrganization({
    query: { organizationId },
  });
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code });
  return (data?.members ?? []) as unknown as OrgMemberRow[];
}

export async function updateOrgMemberRole(
  organizationId: string,
  input: { memberId: string; role: 'owner' | 'admin' | 'member' }
): Promise<void> {
  const { error } = await authClient.organization.updateMemberRole({
    memberId: input.memberId,
    role: input.role,
    organizationId,
  });
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code });
}

export async function removeOrgMember(organizationId: string, memberIdOrEmail: string): Promise<void> {
  const { error } = await authClient.organization.removeMember({ memberIdOrEmail, organizationId });
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code });
}

export function useOrgMembers(organizationId: string | null) {
  return useQuery({
    queryKey: [QUERIES.ORG_MEMBERS, organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => fetchOrgMembers(organizationId!),
  });
}

export function useUpdateMemberRole(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; role: 'owner' | 'admin' | 'member' }) =>
      updateOrgMemberRole(organizationId!, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.ORG_MEMBERS, organizationId] }),
  });
}

export function useRemoveMember(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberIdOrEmail: string) => removeOrgMember(organizationId!, memberIdOrEmail),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.ORG_MEMBERS, organizationId] }),
  });
}
